import React, { useEffect } from "react";
import { connect } from "react-redux";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { commonCountryServices } from "./api/useQueryHooks";
import { initCountryData } from "./constants";


import RestIdLayout from "./components/RestIdLayout";
import SessionTimeout from "./components/SessionTimeout";
import Login from "./containers/Auth/Login";
import Logout from "./containers/Auth/Logout";
import Categories from "./containers/Category/Category";
import { GlobalCustomersRoutes } from "./containers/Customers/routes";
import AddEvent from "./containers/Event/AddEvent";
import Events from "./containers/Event/Events";
import FeaturesList from "./containers/GlobalSettings/FeaturesList/FeaturesList";
import AccountDetail from "./containers/Restaurant/AccountDetail";
import AddArModel from "./containers/Restaurant/AddArModel";
import CreateArModel from "./containers/Restaurant/CreateArModel";
import AddRestaurant from "./containers/Restaurant/AddRestaurantInfo";
import GlobalSettings from "./containers/Restaurant/GlobalSettings";
import Restaurants from "./containers/Restaurant/Restaurants";
import SalesReport from "./containers/Restaurant/Sales/SalesReport";
import Settings from "./containers/Restaurant/Settings";
import UberEats from "./containers/Restaurant/UberEats";
import Users from "./containers/User/Users";
import RestaurantInfo from "./containers/Restaurant/RestaurantInfo";
import SettingsMenu from "./containers/Restaurant/manage-menu/menu/pages/SettingsMenu";
import ManageMenu from "./containers/Restaurant/ManageMenu";
import ArModels from "./containers/Restaurant/ArModels";
import ArModelSelectMenu from "./containers/Restaurant/ArModelSelectMenu";
import AddCategory from "./containers/Restaurant/AddCategory";
import AddMenu from "./containers/Restaurant/manage-menu/menu/pages/AddMenu";
import CategoryReorder from "./containers/Restaurant/CategoryReorder";
// import Menu from "./containers/Restaurant/Menu";
import RestaurantEvents from "./containers/Restaurant/Events";
import GiftCards from "./containers/Restaurant/GiftCards/GiftCards";
import GiftCardTransactions from "./containers/Restaurant/GiftCards/GiftCardTransactions";
import OrderHistory from "./containers/Restaurant/OrderHistory";
import Report from "./containers/Restaurant/Sales/Report";
import Subscriptions from "./containers/Restaurant/Subscriptions";
import CustomRestaurantInfo from "./containers/Restaurant/CustomRestaurantInfo";
import Version from "./containers/Version";
import TableSetupSettings from "./containers/Restaurant/TableSetupSettings";
import TableBookingRestaurantSettings from "./containers/Restaurant/table_booking_settings/TableBookingRestaurantSettings";
import GiftCardSettings from "./containers/Restaurant/giftcard_settings";
import StripeSettings from "./containers/Restaurant/StripeSettings";
import TakeAwaySettings from "./containers/Restaurant/TakeAwaySettings";
import RestaurantFeaturesList from "./containers/Restaurant/FeaturesList";
import Roles from "./containers/Role/Roles";
import ImportExportFoodMenu from "./containers/Restaurant/ImportExportFoodMenu";
import OrderTimeSettings from "./containers/Restaurant/OrderTime/OrderTimeSettings";
import Modifiers from "./containers/Restaurant/Modifiers";
import Course from "./containers/Restaurant/Course/Course";
import StaffManagement from "./containers/Restaurant/Staff_Management/StaffManagement";
import FloorManagement from "./containers/Restaurant/Floor_Management";
import Charges from "./containers/Restaurant/Charges";
import Printers from "./containers/Restaurant/Printers";
import { UiConfigGlobalPage, TableOrderingMenuLayout } from "./containers/UiConfigGlobal/page";
import { AllergensRoutes } from "./containers/Restaurant/allergens/routes";
import { AttributesRoutes } from "./containers/Restaurant/menu-attributes/routes";
import { CustomersRoutes } from "./containers/Restaurant/customers/routes";
import { CouponsRoutes } from "./containers/Restaurant/coupons/routes";
import { DiscountsRoutes } from "./containers/Restaurant/discounts/routes";
import { PaymentProvidersRoutes } from "./containers/PaymentProviders/routes";
import { RestaurantPaymentProvidersRoutes } from "./containers/Restaurant/payment-providers/routes";
import Dashboard from "./containers/Dashboard/Dashboard";
import SubscriptionsPage from "./containers/Subscriptions/index";
import Socials from "./containers/Socials/Socials";
import ContentGenerations from "./containers/Content/ContentGenerations";
import ReferenceContentManager from "./containers/Content/ReferenceContentManager";
import ContentManager from "./containers/Content/ContentManager";
import ContentManagerHistory from "./containers/Content/ContentManagerHistory";

const App = (props) => {
  const { data: countryList, isError: countriesError, refetch: refetchCountries } = commonCountryServices.useGetCountryList();

  useEffect(() => {
    if (countryList?.length) initCountryData(countryList);
  }, [countryList]);

  useEffect(() => {
    if (!countriesError) return;
    console.warn("[App] /countries/all failed after retries — falling back to hardcoded currency/country values. Will retry on next window focus.");
    const retry = () => { if (document.visibilityState === "visible") refetchCountries(); };
    document.addEventListener("visibilitychange", retry, { once: true });
    return () => document.removeEventListener("visibilitychange", retry);
  }, [countriesError]);

  let routes = (
    <Routes>
      <Route element={<RestIdLayout />}>
        <Route exact path="/login" element={<Login {...props} />} />
        <Route exact path="/" element={<Navigate replace to="/login" />} />
        <Route path="/version" element={<Version />} />
        <Route path="*" element={<Navigate replace to="/login" />} />
      </Route>
    </Routes>
  );

  if (props.userToken) {
    routes = (
      <Routes>
        <Route element={<RestIdLayout />}>
          <Route exact path="/dashboard" element={<Dashboard {...props} />} />
          <Route exact path="/subscriptions" element={<SubscriptionsPage {...props} />} />
          <Route exact path="/socials" element={<Socials {...props} />} />
          <Route
            exact
            path="/content-generations"
            element={<ContentGenerations {...props} />}
          />
          <Route
            exact
            path="/reference-content-manager"
            element={<ReferenceContentManager {...props} />}
          />
          <Route
            exact
            path="/content-manager"
            element={<ContentManager {...props} />}
          />
          <Route
            exact
            path="/content-manager/:restaurantId"
            element={<ContentManagerHistory {...props} />}
          />
          <Route exact path="/events" element={<Events {...props} />} />
          <Route exact path="/add_event" element={<AddEvent {...props} />} />
          <Route
            exact
            path="/global_settings"
            element={<GlobalSettings {...props} />}
          />
          <Route exact path="/" element={<Navigate replace to="/dashboard" />} />
          <Route
            exact
            path="/restaurants"
            element={<Restaurants {...props} />}
          />
          <Route exact path="/categories" element={<Categories {...props} />} />
          <Route path="/customers/*" element={<GlobalCustomersRoutes />} />
          <Route
            exact
            path="/add_restaurant"
            element={<AddRestaurant {...props} />}
          />
          <Route exact path="/users" element={<Users {...props} />} />
          <Route exact path="/roles" element={<Roles {...props} />} />
          {/*<Route*/}
          {/*  exact*/}
          {/*  path="/restaurant/:rest_id/menu"*/}
          {/*  element={<Menu {...props} />}*/}
          {/*/>*/}
          <Route
            exact
            path="/restaurant/:rest_id/events"
            element={<RestaurantEvents {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id"
            element={<Settings {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/settings"
            element={<SettingsMenu {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/settings/menu"
            element={<SettingsMenu {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/settings/category"
            element={<SettingsMenu {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/manage_menu"
            element={<ManageMenu {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/info/"
            element={<RestaurantInfo {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/custom_info/"
            element={<CustomRestaurantInfo {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/ar_models"
            element={<ArModels {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/select_menu"
            element={<ArModelSelectMenu {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/add_model"
            element={<CreateArModel {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/model/:id"
            element={<AddArModel {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/add_category"
            element={<AddCategory {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/add_category/:id"
            element={<AddCategory {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/add_menu"
            element={<AddMenu {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/add_menu/:id"
            element={<AddMenu {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/category_reorder"
            element={<CategoryReorder {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/uber_eats"
            element={<UberEats {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/swiggy"
            element={<UberEats {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/foodo_menu"
            element={<ImportExportFoodMenu {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/course"
            element={<Course {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/modifiers"
            element={<Modifiers {...props} />}
          />
          <Route path="/restaurant/:rest_id/allergens/*" element={<AllergensRoutes />} />
          <Route path="/restaurant/:rest_id/menu-attributes/*" element={<AttributesRoutes />} />
          <Route path="/restaurant/:rest_id/customers/*" element={<CustomersRoutes />} />
          <Route path="/restaurant/:rest_id/discounts/*" element={<DiscountsRoutes />} />
          <Route path="/restaurant/:rest_id/coupons/*" element={<CouponsRoutes />} />
          <Route
            path="/restaurant/:rest_id/payment_providers/*"
            element={<RestaurantPaymentProvidersRoutes />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/account_detail"
            element={<AccountDetail {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/order_history"
            element={<OrderHistory {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/sales"
            element={<SalesReport {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/sales/x_report"
            element={<Report {...props} isXReport />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/sales/z_report"
            element={<Report {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/manage_staffs"
            element={<StaffManagement {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/floor_management"
            element={<FloorManagement {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/gift_card"
            element={<GiftCards {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/gift_card/transactions"
            element={<GiftCardTransactions {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/subscriptions"
            element={<Subscriptions {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/table_setup_settings"
            element={<TableSetupSettings {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/table-booking-settings"
            element={<TableBookingRestaurantSettings {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/giftcard-settings"
            element={<GiftCardSettings {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/stripe_settings"
            element={<StripeSettings {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/takeaway_settings"
            element={<TakeAwaySettings {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/features_list"
            element={<RestaurantFeaturesList {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/charges"
            element={<Charges {...props} />}
          />
          <Route
            exact
            path="/restaurant/:rest_id/printers"
            element={<Printers {...props} />}
          />
          <Route
            exact
            path="/global_settings/features_list"
            element={<FeaturesList {...props} />}
          />
          <Route
            exact
            path="/ui-config-global"
            element={<UiConfigGlobalPage {...props} />}
          />
          <Route
            exact
            path="/ui-config-global/table-ordering-menu-layout"
            element={<TableOrderingMenuLayout {...props} />}
          />
          <Route path="/payment_providers/*" element={<PaymentProvidersRoutes />} />
          <Route exact path="/logout" element={<Logout {...props} />} />
          <Route path="/version" element={<Version />} />
          {/* <Route path="*" element={<Navigate replace to="/" />} /> */}
        </Route>
      </Routes>
    );
  }

  return (
    <BrowserRouter>
      <SessionTimeout userToken={props.userToken}>{routes}</SessionTimeout>
    </BrowserRouter>
  );
};

const mapStateToProps = (state) => {
  return {
    userToken: state.userData.userToken,
  };
};

export default connect(mapStateToProps, null)(App);
